import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = (returnTo = "/") => {
  try { sessionStorage.setItem("kandula-return-to", returnTo); } catch { /* Optional navigation preference. */ }
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthProvider = ({ children }) => {
  const [user, updateUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0), controller = useRef(null);
  const cancel = useCallback(() => { generation.current++; controller.current?.abort(); }, []);
  const setUser = useCallback((value) => { cancel(); updateUser(value); }, [cancel]);
  const refresh = useCallback(async () => {
    cancel(); const current = generation.current;
    controller.current = new AbortController();
    try {
      const { data } = await axios.get(`${API}/auth/me`, { signal: controller.current.signal });
      if (current === generation.current) updateUser(data);
      return data;
    } catch {
      if (current === generation.current) updateUser(null);
      return null;
    } finally { if (current === generation.current) setLoading(false); }
  }, [cancel]);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) setLoading(false);
    else refresh();
    return cancel;
  }, [refresh, cancel]);
  const logout = useCallback(async () => {
    cancel(); const current = generation.current;
    try {
      await axios.post(`${API}/auth/logout`);
      if (current === generation.current) updateUser(null);
    } catch { toast.error("Could not sign out. Please check your connection and try again."); }
  }, [cancel]);
  const updateProfile = useCallback(async (data) => {
    cancel(); const current = generation.current;
    const response = await axios.patch(`${API}/auth/profile`, data);
    if (current === generation.current) updateUser(response.data);
    return response.data;
  }, [cancel]);
  const value = useMemo(() => ({ user, loading, refresh, setUser, logout, updateProfile, login: startGoogleLogin }), [user, loading, refresh, setUser, logout, updateProfile]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};